import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GripVertical, Loader2, Music2, Radio, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';

/**
 * Parts shared by the Music page (pages/Music.tsx) and its Playlists tab
 * (pages/MusicPlaylists.tsx): song types, cover, round icon button, the
 * search box with suggestions and drag-to-reorder for lists.
 */

export interface Song {
  /** Stable id of the queue entry (bot Music/functions/queue.js). */
  uid: string;
  position: number;
  title: string;
  url: string;
  durationSec: number | null;
  channel: string | null;
  thumbnail: string | null;
  requestedBy: { id: string; username: string; avatarUrl: string | null } | null;
  /** Spotify songs play from YouTube but show Spotify's title, artist and link. */
  source?: 'youtube' | 'soundcloud' | 'spotify';
  /** From a playlist / Spotify, not looked up yet (happens shortly before it plays). */
  pending?: boolean;
  /** Radio: the favorite / search hit that plays (pages/MusicRadio.tsx). */
  stationId?: string | null;
}

/** Pasted links play on Enter — no suggestions for them. */
export const isLink = (text: string) => /^(https?:\/\/|spotify:)/i.test(text.trim());

export interface SearchResult {
  id: string;
  title: string;
  channel: string | null;
  duration: number | null;
  url: string;
  thumbnail: string | null;
  source: 'youtube' | 'soundcloud';
}

export function formatTime(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
}

/** From a Spotify link — the sound is the matching YouTube video. */
export function SpotifyBadge() {
  return (
    <span
      className="mr-1.5 rounded bg-[#1db954]/15 px-1 py-px text-[10px] font-semibold text-[#1db954]"
      title="Von Spotify — abgespielt wird der passende Song von YouTube"
    >
      Spotify
    </span>
  );
}

export function DropLine({ position }: { position: 'top' | 'bottom' }) {
  return <span className={cn('pointer-events-none absolute inset-x-0 h-0.5 bg-primary', position === 'top' ? '-top-px' : '-bottom-px')} />;
}

/**
 * Song cover (YouTube thumbnail / SoundCloud artwork), placeholder otherwise.
 * Radio logos are shown whole on a dark backing (often square or wide).
 */
export function Cover({ src, radio, className }: { src: string | null; radio?: boolean; className?: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        className={cn('shrink-0 rounded-md shadow-sm', radio ? 'bg-black/30 object-contain p-1' : 'object-cover', className)}
      />
    );
  }
  return (
    <div className={cn('flex shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary', className)}>
      {radio ? <Radio className="h-1/3 w-1/3" /> : <Music2 className="h-1/3 w-1/3" />}
    </div>
  );
}

export function IconButton({
  label,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-40',
        className
      )}
      {...props}
    />
  );
}

// --------------------------- Search with suggestions ---------------------------

/**
 * Search box: after a short typing pause, 5 hits from YouTube (SoundCloud
 * when YouTube has none) with cover, channel and length. A click plays
 * exactly that song; Enter without a pick plays the first hit as before.
 *
 * With `onPickResult` (playlists) a click / Enter hands over the hit instead;
 * a pasted link still goes to `onPlay` (there: take over that playlist).
 */
export function SongSearch({
  guildId,
  canPlay,
  busy,
  onPlay,
  onPickResult,
  placeholder = 'Song suchen oder Link einfügen (YouTube, Spotify)',
  linkHint = 'spielt den Link ab — auch Spotify-Songs, -Alben und -Playlists sowie YouTube-Playlists (bis 100 Songs, hinten angehängt).',
}: {
  guildId: string;
  canPlay: boolean;
  busy: boolean;
  onPlay: (query: string) => void;
  onPickResult?: (result: SearchResult) => void;
  placeholder?: string;
  linkHint?: string;
}) {
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    const t = setTimeout(() => setTerm(text.trim()), 400);
    return () => clearTimeout(t);
  }, [text]);
  useEffect(() => setActive(-1), [term]);

  const link = isLink(text);
  const search = useQuery({
    queryKey: ['music-search', guildId, term],
    queryFn: () =>
      api<{ results: SearchResult[] }>(`/api/guilds/${guildId}/music/search?q=${encodeURIComponent(term)}`),
    enabled: term.length >= 2 && !isLink(term),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const results = term === text.trim() && !link ? search.data?.results ?? [] : [];
  const waiting = !link && text.trim().length >= 2 && (term !== text.trim() || search.isFetching);
  const showList = open && text.trim().length >= 2;

  const play = (query: string, result?: SearchResult) => {
    if (!canPlay || busy || !query.trim()) return;
    // A picked hit has a link as query too — only typed/pasted links import.
    if (onPickResult && (result || !isLink(query))) {
      const pick = result ?? results[0];
      if (!pick) return;
      onPickResult(pick);
    } else {
      onPlay(query.trim());
    }
    setText('');
    setOpen(false);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setOpen(false), 150);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              setActive((a) => Math.min(results.length - 1, a + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((a) => Math.max(-1, a - 1));
            } else if (e.key === 'Escape') {
              setOpen(false);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              const picked = active >= 0 ? results[active] : undefined;
              play(picked ? picked.url : text, picked);
            }
          }}
          placeholder={placeholder}
          className="pl-9 pr-9"
          role="combobox"
          aria-expanded={showList}
          aria-autocomplete="list"
        />
        {(waiting || busy) && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {showList && (
        <div
          role="listbox"
          className="glass-float absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-lg border bg-card shadow-xl"
          onMouseDown={() => clearTimeout(blurTimer.current)}
        >
          {!canPlay && (
            <p className="border-b px-3 py-2 text-xs text-muted-foreground">Erst einen Voice-Channel wählen, dann ist ein Klick „abspielen“.</p>
          )}
          {link && (
            <p className="px-3 py-3 text-sm text-muted-foreground">
              <b className="text-foreground">Enter</b> {linkHint}
            </p>
          )}
          {waiting && results.length === 0 && (
            <p className="px-3 py-3 text-sm text-muted-foreground">Suche…</p>
          )}
          {!waiting && search.error && (
            <p className="px-3 py-3 text-sm text-destructive">{(search.error as Error).message}</p>
          )}
          {!link && !waiting && !search.error && results.length === 0 && (
            <p className="px-3 py-3 text-sm text-muted-foreground">
              {onPickResult ? 'Nichts gefunden.' : 'Nichts gefunden — Enter spielt trotzdem den ersten Treffer.'}
            </p>
          )}
          {results.map((r, i) => (
            <button
              key={r.id}
              type="button"
              role="option"
              aria-selected={i === active}
              disabled={!canPlay || busy}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => play(r.url, r)}
              className={cn(
                'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors disabled:cursor-not-allowed',
                i === active ? 'bg-accent' : 'hover:bg-accent/60'
              )}
            >
              <Cover src={r.thumbnail} className="h-10 w-[72px]" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{r.title}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {[r.channel, r.source === 'soundcloud' && 'SoundCloud'].filter(Boolean).join(' · ')}
                </span>
              </span>
              {r.duration != null && (
                <span className="shrink-0 font-mono text-xs text-muted-foreground">{formatTime(r.duration)}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Reordering by dragging a handle — mouse and finger alike (pointer events,
 * no library). `over` is the gap the row would land in (0 … count); onDrop
 * gets the old and the new index.
 */
export function useDragOrder(count: number, onDrop: (from: number, to: number) => void) {
  const listRef = useRef<HTMLUListElement>(null);
  const [drag, setDrag] = useState<{ from: number; over: number } | null>(null);

  const overAt = (clientY: number) => {
    const rows = [...(listRef.current?.children ?? [])] as HTMLElement[];
    const i = rows.findIndex((r) => {
      const box = r.getBoundingClientRect();
      return clientY < box.top + box.height / 2;
    });
    return i === -1 ? rows.length : i;
  };
  const handle = (i: number) => ({
    onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      // Moves keep coming to the handle while the pointer is elsewhere in the list.
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // pointer already gone
      }
      setDrag({ from: i, over: i });
    },
    onPointerMove: (e: React.PointerEvent<HTMLButtonElement>) => {
      if (!drag) return;
      const over = overAt(e.clientY);
      if (over !== drag.over) setDrag({ ...drag, over });
    },
    onPointerUp: () => {
      if (!drag) return;
      const to = drag.over > drag.from ? drag.over - 1 : drag.over;
      setDrag(null);
      if (to !== drag.from) onDrop(drag.from, to);
    },
    onPointerCancel: () => setDrag(null),
  });
  const noChange = !drag || drag.over === drag.from || drag.over === drag.from + 1;
  /** Where the drop line shows for row i. */
  const lineAt = (i: number): 'top' | 'bottom' | null =>
    noChange ? null : drag!.over === i ? 'top' : drag!.over === count && i === count - 1 ? 'bottom' : null;
  return { listRef, draggingIndex: drag?.from ?? null, handle, lineAt };
}

export function DragHandle({ disabled, ...props }: ReturnType<ReturnType<typeof useDragOrder>['handle']> & { disabled?: boolean }) {
  return (
    <button
      type="button"
      aria-label="Ziehen zum Verschieben"
      title="Ziehen zum Verschieben"
      disabled={disabled}
      {...props}
      className="flex h-8 w-6 shrink-0 cursor-grab touch-none items-center justify-center text-muted-foreground hover:text-foreground active:cursor-grabbing disabled:opacity-40"
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );
}
